import { observable, action, makeObservable } from 'mobx';
import { notify } from 'services/NotificationService';
import { FilterChip } from 'components/sessionTable/TableFilters';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { DateFormat } from 'constants/DateFormat';
import { SessionFilters } from '../components/sessionTable/SessionFilters';
import { ILevel, ISession, ILeague, IField, IVenue } from '../interfaces/session/ISession';
import { BaseStore } from './BaseStore';
import { SessionVerificationApiService } from '../services/data-services/SessionVerificationService';
import { IUmpire } from 'interfaces/umpire/IUmpire';
import { IFilterableStore } from 'interfaces/IFilterableStore';
import uuid from 'uuid';
import { ISessionLockMessage } from 'interfaces/session/ISessionLock';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const IS_SESSION_LOCK_ENABLED = true;
export class SessionVerificationStore extends BaseStore implements IFilterableStore<ISession> {
  private sessionVerifierService: SessionVerificationApiService;

  @observable sessions: ISession[] = [];

  @observable cachedSessions: ISession[] = [];

  @observable leagues: ILeague[] = [];

  @observable levels: ILevel[] = [];

  @observable fields: IField[] = [];

  @observable venues: IVenue[] = [];

  @observable filters = [] as FilterChip[];

  @observable umpires = [] as IUmpire[];

  @observable page = 1;

  @observable isLockMessageReceived: any = uuid();

  constructor(sessionVerifierService: SessionVerificationApiService) {
    super();
    this.sessionVerifierService = sessionVerifierService;
    makeObservable(this);
  }

  @action setSessions = (data: any) => {
    this.sessions = data;
    this.cachedSessions = data;
  };

  @action setPage = (value: number) => {
    this.page = value;
  };

  @action fetchSessions = async (date: string, teamId: string) => {
    try {
      await this.fetchData(() => this.sessionVerifierService.getSessions(date, teamId), this.setSessions);
    } catch (ex: any) {
      notify('Error', ex.message, 'error');
    }
  };

  @action fetchContextualData = async () => {
    try {
      [this.leagues, this.levels, this.venues, this.fields, this.umpires] = await Promise.all<any>([
        this.sessionVerifierService.getLeagues(),
        this.sessionVerifierService.getLevels(),
        this.sessionVerifierService.getVenues(),
        this.sessionVerifierService.getFields(),
        this.sessionVerifierService.GetUmpires(),
      ]);
    } catch (ex: any) {
      notify('Error', ex.message, 'error');
    }
  };

  @action getContextualData = async () => {
    if (this.leagues.length > 0 && this.levels.length > 0 && this.venues.length > 0 && this.fields.length > 0) {
      return;
    }

    await this.fetchContextualData();
  };

  @action fetchOwningOrganizations = async (fieldId: string) => {
    try {
      return await this.sessionVerifierService.getOwningOrganizations(fieldId);
    } catch (e: any) {
      notify('Error', e.message, 'error');
      return [];
    }
  };

  @action setLoading = (value: boolean): void => {
    this.isLoading = value;
  };

  @action addFilter = (value: FilterChip): void => {
    this.filters.forEach((filter, index) => {
      if (filter.id === value.id) {
        this.filters.splice(index, 1);
      }
    });

    this.filters.push(value);
    this.filterSessions();
    this.setPage(1);
  };

  @action removeFilter = (index: number): void => {
    this.filters.splice(index, 1);
    this.setLoading(true);
    this.filterSessions();
    this.setLoading(false);
    this.setPage(1);
  };

  findMatchingFilters = (session: ISession): boolean[] => {
    const tempArr: boolean[] = [];
    this.filters.forEach((filter: FilterChip) => {
      switch (filter.id) {
        case SessionFilters.LocalStartTime: {
          const splittedDate = filter.value.split(' ');
          const sessionDate = dayjs(session[filter.id]).format(DateFormat.queryFilterFormat);
          return tempArr.push(dayjs(splittedDate[0]).isSameOrBefore(sessionDate) && dayjs(splittedDate[1]).isSameOrAfter(sessionDate));
        }
        case SessionFilters.Venue:
        case SessionFilters.Field:
        case SessionFilters.HomeTeam:
        case SessionFilters.Level:
        case SessionFilters.League: {
          return tempArr.push(session[filter.id]?.name.toLowerCase().indexOf(filter.value.toLowerCase()) > -1);
        }
        case SessionFilters.GameReference: {
          return tempArr.push(session[filter.id].toLowerCase().indexOf(filter.value.toLowerCase()) > -1);
        }
      }
    });

    return tempArr;
  };

  @action filterSessions = (): void => {
    const filterResult = this.cachedSessions.filter((session: ISession): boolean => {
      const tempArr: boolean[] = this.findMatchingFilters(session);

      return tempArr.every((item: boolean) => item === true);
    });
    this.sessions = filterResult;
  };

  verifySessionProperties = (session: ISession) => {
    if (session.isBattingPractice || !session?.awayTeam?.id || !session?.homeTeam?.id) {
      return false;
    }
    return true;
  };

  validateExternalSessionId = async (externalSessionId: string) => {
    return this.sessionVerifierService.validateExternalSessionId(externalSessionId);
  };

  fetchUmpires = async (keyword: string) => {
    const data = await this.sessionVerifierService.GetUmpires();
    this.umpires = data;
  };

  handleLockedSession = (message: ISessionLockMessage) => {
    if (IS_SESSION_LOCK_ENABLED) {
      const sessionToUpdate = this.sessions.find((session) => session.id === message.lockState.sessionId);

      if (sessionToUpdate?.id) {
        sessionToUpdate.isLocked = message.isLocked;

        this.sessions = this.sessions.map((session) => (session.id === sessionToUpdate.id ? sessionToUpdate : session));
      }

      this.isLockMessageReceived = uuid();
    }
  };
}
